import { Autocomplete, Checkbox, FormControl, FormHelperText, FormLabel, Textarea } from '@mui/joy';
import { useContext, useEffect, useMemo } from 'react';
import { lowerFirst } from 'lodash';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { type AutocompleteOption, type TAutocompleteOption } from '~/common/types';
import { type CompanyFormValues } from './useAddEditCompanyFormConfig';
import { type ReduxStateProps } from '@/redux/reducers/types';

import { CaseRole, ExactOnlinePaymentCondition, Language } from '~/common/enums';
import { AppContext } from '@/shared/context/context';
import { trpc } from '@/config/trpc';

import { sentencize } from '@/shared/utils/helpers';

import { useAutocompleteOptions } from '@/shared/hooks/autocomplete';
import useToggle from '@/shared/hooks/UseToggle';

import AddressesInput from './AddressesInput';
import ConfirmationModal from '@/shared/components/ConfirmationModal';
import EmailAddressInput from '@/containers/Email/EmailAddressInput';
import FormikHomeownerAssociationSelector from '@/shared/components/2.0/formik/FormikHomeownerAssociationSelector';
import FormikIbansInput from '@/shared/components/2.0/input/FormikIbansInput';
import FormikMultiPhoneNumbersInput from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbersInput';
import FormikTextField from '@/shared/components/2.0/formik/FormikTextField';
import FormikVatInput from '@/shared/formik/FormikVatInput';

type Props = {
  minified?: boolean;
  detailed?: boolean;
  setHasManualError?: React.Dispatch<React.SetStateAction<boolean>>;
};

const AddEditCompanyFormBody: React.FC<Props> = ({
  detailed = false,
  minified = false,
  setHasManualError = () => {},
}) => {
  const { currentTeam } = useContext(AppContext);
  const { t } = useTranslation(['common', 'errors']);
  const { values, setFieldValue, setFieldTouched, handleChange, errors, touched } =
    useFormikContext<CompanyFormValues>();

  const { hasActiveExactOnlineIntegration } = useSelector(
    (state: ReduxStateProps) => state.integrations,
  );

  const exactOnlinePaymentConditionOptions = Object.values(ExactOnlinePaymentCondition).map(
    (pc) => ({
      label: t(`_exact.paymentCondition.${lowerFirst(pc)}`),
      value: pc,
    }),
  ) as TAutocompleteOption<ExactOnlinePaymentCondition>[];

  const languageOptions = Object.values(Language).map((l) => ({
    label: t(lowerFirst(l)),
    value: l,
  })) as TAutocompleteOption<Language>[];

  const caseRoleOptions = Object.values(CaseRole).map((role) => ({
    label: t(lowerFirst(role)),
    value: role,
  })) as TAutocompleteOption<CaseRole>[];

  const { data: tags } = trpc.company.tags.useQuery();

  const autocompleteProps = {
    language: useAutocompleteOptions({
      callback: async (option) => {
        await setFieldValue('language', option?.value);
        if (values.secondaryLanguages.find((l) => l === option?.value)) {
          setFieldValue(
            'secondaryLanguages',
            values.secondaryLanguages.filter((l) => l !== option?.value),
          );
        }
        setFieldTouched('language');
      },
      data: languageOptions,
      selected: values.language as Language | undefined,
    }),
    secondaryLanguages: useAutocompleteOptions({
      callback: (options) =>
        setFieldValue(
          'secondaryLanguages',
          options.map((option) => option.value),
        ),
      data: languageOptions.filter((option) => option.value !== values.language),
      multiple: true,
      selected: values.secondaryLanguages,
    }),
    tags: useAutocompleteOptions({
      callback: (options: AutocompleteOption[]) =>
        setFieldValue(
          'companyTags',
          options.map((option) => option.value),
        ),
      data: tags,
      multiple: true,
      selected: values.companyTags,
    }),
  };

  const directDebitToggle = useToggle();

  const hasError = (name: keyof CompanyFormValues) => !!touched[name] && !!errors[name];
  const selectedCaseRole = useMemo(
    () => caseRoleOptions.find((role) => role.value === values.defaultCaseRole),
    [caseRoleOptions, values.defaultCaseRole],
  );

  useEffect(() => {
    setHasManualError(!!errors.vatNumber);
  }, [errors.vatNumber, setHasManualError]);

  const allowSyndicManagement = !!currentTeam?.allowSyndicManagement;
  const allowRepairManagement = !!currentTeam?.allowRepairManagement;
  const allowCaseManagement = !!currentTeam?.allowCaseManagement;

  return (
    <div className="flex flex-col gap-4">
      <FormikVatInput name="vatNumber" />
      <FormikTextField name="name" label={t('displayName')} required />
      <div className={allowCaseManagement ? 'flex gap-4' : ''}>
        <div className={allowCaseManagement ? 'w-1/2' : ''}>
          <FormControl>
            <FormLabel>{t('tags')}</FormLabel>
            <Autocomplete {...autocompleteProps.tags} />
          </FormControl>
        </div>
        {allowCaseManagement && (
          <div className="w-1/2">
            <FormControl>
              <FormLabel>{t('defaultRole')}</FormLabel>
              <Autocomplete
                options={caseRoleOptions}
                value={selectedCaseRole}
                onChange={(_, value) => {
                  setFieldValue('defaultCaseRole', value?.value || null);
                }}
              />
            </FormControl>
          </div>
        )}
      </div>
      {!minified && (
        <>
          {allowRepairManagement && (
            <FormikTextField
              name="externalReference"
              label={t('_company.forms.addEdit.externalReference')}
            />
          )}
          {allowSyndicManagement && (
            <>
              <div className="flex flex-wrap items-end gap-4 *:min-w-48">
                <FormikTextField
                  min={0}
                  name="paymentPeriod"
                  label={sentencize(`${t('paymentPeriod')} (${t('day_other')})`)}
                  type="number"
                />
                <div className="p-0.5">
                  <Checkbox
                    checked={values.directDebitEnabled}
                    label={t('directDebit')}
                    onChange={(e) => {
                      const { checked } = e.target;

                      if (checked) {
                        directDebitToggle.show();
                      } else {
                        setFieldValue('directDebitHomeownerAssociationIds', []);
                        setFieldValue('directDebitEnabled', checked);
                      }
                    }}
                  />
                </div>
              </div>
              {values.directDebitEnabled && (
                <div className="flex gap-4">
                  <FormikHomeownerAssociationSelector
                    label={t('homeownerAssociationsWhereDirectDebitIsEnabled')}
                    isRequired
                    name="directDebitHomeownerAssociationIds"
                    multiple
                    withSelectAllButton
                  />
                </div>
              )}
            </>
          )}

          {allowRepairManagement && hasActiveExactOnlineIntegration && (
            <FormControl>
              <FormLabel>{t('paymentCondition')}</FormLabel>
              <Autocomplete
                onChange={(_, value) =>
                  setFieldValue('exactOnlinePaymentCondition', value?.value || null)
                }
                value={
                  exactOnlinePaymentConditionOptions.find(
                    (option) => option.value === values.exactOnlinePaymentCondition,
                  ) || null
                }
                options={exactOnlinePaymentConditionOptions}
                name="exactOnlinePaymentCondition"
                placeholder={t('paymentCondition')}
              />
            </FormControl>
          )}

          {detailed && allowRepairManagement && (
            <FormControl className="pb-2">
              <Checkbox
                name="isActive"
                onChange={handleChange}
                checked={values.isActive}
                label={t('active')}
              />
            </FormControl>
          )}

          <FormikIbansInput className="mb-2 mt-0" />

          <FormikMultiPhoneNumbersInput />

          <EmailAddressInput className="mb-2 mt-0" />

          {!values.companyId && <AddressesInput className="mb-2 mt-0" />}

          <FormControl error={hasError('language')}>
            <FormLabel required>{t('language')}</FormLabel>
            <Autocomplete {...autocompleteProps.language} />
            {hasError('language') && (
              <FormHelperText className="pl-1 text-xs">{errors.language}</FormHelperText>
            )}
          </FormControl>

          {values.isMemberOfContractorNetwork && (
            <FormControl>
              <FormLabel>{t('secondaryLanguages')}</FormLabel>
              <Autocomplete {...autocompleteProps.secondaryLanguages} />
            </FormControl>
          )}

          <FormControl>
            <FormLabel>{t('note')}</FormLabel>
            <Textarea
              value={values.note || ''}
              onChange={(e) => setFieldValue('note', e.target.value)}
            />
          </FormControl>

          {detailed && (
            <FormControl>
              <Checkbox
                name="pushCrmChanges"
                onChange={handleChange}
                checked={values.pushCrmChanges}
                label={t('pushCrmChanges')}
                className="ml-auto"
              />
            </FormControl>
          )}
          <ConfirmationModal
            toggle={directDebitToggle}
            onConfirm={() => setFieldValue('directDebitEnabled', true)}
          >
            {t('directDebitWillBeTurnedOn')}
          </ConfirmationModal>
        </>
      )}
    </div>
  );
};

export default AddEditCompanyFormBody;
